import { withTransaction } from '@elastic/apm-rum-react'
import {
  AnswersFormated,
  AnswersPayload,
  DakenService,
  IBGEService,
  InsideNavigationParcelComponent,
  localForageHelper,
  LogService,
  MenuItemNavigation,
  NavigationLayout,
  onLangChange,
  onProfileCompletenessChange,
  onProfilePercentageChange,
  onUserChange,
  QuestionGroupedInterface,
  ToastHelper,
  User,
} from '@hub/shared'
import { SqCircleCompletation, SqLoader } from '@squidit/react-css'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import GroupQuestionsComponent from './components/group-questions.component'
import './profile-progressive.page.scoped.scss'

interface CompletenessObject {
  [param: string]: {
    [param: string]: boolean
  }
}

const ProfileProgressivePage = (props: any) => {
  const onLangChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [user, setUser] = useState<User>()
  const toast = new ToastHelper().toast
  const [valid, setValid] = useState(false)
  const [data, setData] = useState<AnswersFormated>({})
  const [t, i18n] = useTranslation('profileProgressivePage')
  const dakenService = useMemo(() => new DakenService(), [])
  const [impersonateObject, setImpersonateObject] = useState<any>(null)
  const [percentageProfileCompleteness, setUserPercentageProfileCompleteness] = useState<number>(-1)
  const [completenessObject, setCompletenessObject] = useState<CompletenessObject>({} as CompletenessObject)
  const logService = useMemo(() => new LogService(), [])
  const [loading, setLoading] = useState(true)
  const ibgeService = useMemo(() => new IBGEService(), [])
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [loadingAnswers, setLoadingAnswers] = useState(false)
  const [groups, setGroups] = useState<QuestionGroupedInterface[]>([])
  const location = useLocation()
  const [menu, setMenu] = useState<Array<MenuItemNavigation>>([])
  const [language, setLanguage] = useState('en')

  const homeNavigationPath = '/profile/progressive'

  logService.init('Profile Progressive Page')

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe((user) => {
      setUser(user)
      if (user?.squidId && percentageProfileCompleteness < 0) {
        dakenService.getInflucencersCompleteness(user?.squidId).then((r) => {
          setUserPercentageProfileCompleteness(r?.progressive?.percentage ?? 0)
          onProfileCompletenessChange.next(r)
          onProfilePercentageChange.next(r?.percentage)
          setCompletenessObject(r?.progressive?.detail ?? {})
        })
      }
    })
    return () => {
      onUserChangeSubscription?.current?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dakenService, onUserChange])

  useEffect(() => {
    let lang = 'en'
    if (props?.environments?.langs?.length) {
      for (var i = 0; i < props.environments.langs.length; i++) {
        if (i18n.language.startsWith(props.environments.langs[i])) {
          lang = props.environments.langs[i]
          break
        }
      }
    }
    setLanguage(lang)
  }, [i18n.language, props?.environments?.langs])

  useEffect(() => {
    localForageHelper.getItem('impersonateUser').then((impersonateUser) => setImpersonateObject(impersonateUser))
  }, [])

  useEffect(() => {
    onLangChangeSubscription.current = onLangChange?.subscribe((lang: string) => {
      setMenu((m) =>
        m?.map((item: MenuItemNavigation) => {
          return {
            ...item,
            label: groups.find((q) => item?.path?.includes(q.group_id || ''))?.labels[lang],
          }
        })
      )
    })
    return () => {
      onLangChangeSubscription?.current?.unsubscribe()
    }
  }, [onLangChangeSubscription, t, groups])

  const createMenuWarnings = useCallback(() => {
    setMenu((m) => {
      return m?.map((item: MenuItemNavigation) => {
        let hasSomeRequiredFalse = false
        const id: string = item?.path?.split('/').pop() || ''
        if (completenessObject[id]) {
          hasSomeRequiredFalse = Object.keys(completenessObject[id])?.some((key) => !completenessObject[id][key])
        }
        return {
          ...item,
          warning: hasSomeRequiredFalse,
        }
      })
    })
  }, [completenessObject])

  useEffect(() => {
    createMenuWarnings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu.length])

  useEffect(() => {
    createMenuWarnings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completenessObject])

  const transformInMenu = useCallback(
    (questions: QuestionGroupedInterface[]): MenuItemNavigation[] => {
      const lang = language?.slice(0, 2)
      return questions
        .sort((a, b) => {
          if (a.order === b.order) {
            if (a.labels[lang] > b.labels[lang]) {
              return 1
            }
            if (a.labels[lang] < b.labels[lang]) {
              return -1
            }
            return 0
          }
          if (a.order && b.order) {
            if (a.order > b.order) {
              return 1
            }
            if (a.order < b.order) {
              return -1
            }
          }
          return 0
        })
        ?.map((grouped) => {
          return {
            icon: grouped.icon || 'poll-people',
            label: grouped.labels[lang],
            path: `/profile/progressive/${grouped.group_id}`,
          }
        })
    },
    [language]
  )

  useEffect(() => {
    ibgeService
      .getQuestions()
      .then((newQuestions) => {
        setGroups(newQuestions)
        if (!newQuestions?.length) {
          history.pushState(null, '', '/profile')
        }
        setMenu(transformInMenu(newQuestions))
      })
      .catch((err) => {
        logService.error({
          message: 'Error on get questions',
          error: err,
        })
        history.pushState(null, '', '/profile')
      })
      .finally(() => setLoading(false))
      .then(() => {
        setLoadingAnswers(true)
        ibgeService
          .getAnswers()
          .then((answers) => {
            setData(answers)
          })
          .catch((err) => {
            logService.error({
              message: 'Error on get answers',
              error: err,
            })
          })
          .finally(() => setLoadingAnswers(false))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (location.pathname === homeNavigationPath && groups[0]?.group_id && window.innerWidth > 991) {
      history.pushState(null, '', `${homeNavigationPath}/${groups[0]?.group_id}`)
    }
  }, [location.pathname, groups])

  const findTitle = useCallback(
    (path: string) => {
      return menu.find((item) => item.path === path)?.label
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menu, location.pathname]
  )

  const prepareAnswers = (answers: AnswersFormated): AnswersPayload[] => {
    return Object.entries(answers).flatMap(([question, answer]) => {
      if (answer?.options?.length) {
        return answer.options?.map(
          (answer_option) =>
            ({
              question,
              answer: null as string | null,
              answer_option: answer_option.option,
              id: answer_option.id,
            } as AnswersPayload)
        )
      } else {
        return {
          id: answer?.id,
          question,
          answer: answer.answer,
          answer_option: null,
        } as AnswersPayload
      }
    })
  }

  const rollbackAnswers = (answers: AnswersPayload[]): AnswersFormated => {
    return answers.reduce((result, answer) => {
      const { question, answer: answerValue, answer_option: answerOption, id } = answer

      if (!result[question]) {
        result[question] = { answer: answerValue, options: [] }
      }

      if (answerOption) {
        result[question].options.push({
          option: answerOption,
          id,
        })
      } else {
        result[question].answer = answerValue
        result[question].id = id
      }

      return result
    }, {} as AnswersFormated)
  }

  const submit = useCallback(async () => {
    if (!loadingSubmit) {
      try {
        setLoadingSubmit(true)
        const payload: AnswersPayload[] = prepareAnswers(data)
        const response = await ibgeService.putAnswer(payload)
        setData(rollbackAnswers(response))
        if (user?.squidId) {
          dakenService.getInflucencersCompleteness(user?.squidId).then((r) => {
            setUserPercentageProfileCompleteness(r?.progressive?.percentage ?? 0)
            onProfileCompletenessChange.next(r)
            onProfilePercentageChange.next(r?.percentage)
            setCompletenessObject(r?.progressive?.detail ?? {})
          })
        }
        toast.success(t('success'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
      } catch (error) {
        logService.error({
          error,
          message: 'Fail on update Progressive registration',
        })
        toast.error(t('errors.unexpected'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setLoadingSubmit(false)
      }
    }
  }, [loadingSubmit, t, toast, logService, data, ibgeService, dakenService, user?.squidId])

  if (loading) {
    return (
      <div className="wrapper-loader box text-center">
        <SqLoader size="bigger" />
      </div>
    )
  }

  return (
    <div>
      <NavigationLayout
        isHome={location.pathname === homeNavigationPath}
        menu={menu}
        title={t('title')}
        header={<SqCircleCompletation value={percentageProfileCompleteness} />}
      >
        <InsideNavigationParcelComponent
          homeNavigationPath={homeNavigationPath}
          title={findTitle(location.pathname)}
          loadingButton={loadingSubmit}
          disabledButton={!valid || (!!impersonateObject && !impersonateObject?.impersonateAdmin)}
          confirmFunction={(e: any) => submit()}
        >
          <Routes>
            {groups?.map((group) => {
              return (
                <Route
                  path={`/${group.group_id}`}
                  key={group.group_id}
                  element={
                    <GroupQuestionsComponent
                      language={language}
                      questions={group.questions ?? []}
                      pushFormSubmit={submit}
                      setValidate={setValid}
                      setData={setData}
                      loading={loadingSubmit || loadingAnswers}
                      data={data}
                      impersonateUser={impersonateObject}
                    />
                  }
                />
              )
            })}
            <Route path="/" />
            <Route element={<Navigate to={{ pathname: '/error/not-found' }} />} />
          </Routes>
        </InsideNavigationParcelComponent>
      </NavigationLayout>
    </div>
  )
}

export default withTransaction('ProfileProgressivePage', 'component')(ProfileProgressivePage)
